import type { ExtensionDefinition } from './types'

interface PendingRegistration {
    resolve: (definition: ExtensionDefinition) => void
    reject: (error: Error) => void
    timer: ReturnType<typeof setTimeout>
}

// Extensions are classic scripts: the loader waits here for the script to call
// registerExtension() with the id it was loaded for.
const pending = new Map<string, PendingRegistration>()

/**
 * Called by a tenant extension's entry script (via `@distribuai/sdk`).
 * Registrations nobody is waiting for (an unexpected id, a duplicate script) are ignored.
 */
export function registerExtension(definition: ExtensionDefinition): void {
    const waiting = pending.get(definition.id)
    if (!waiting) {
        console.warn(`[extensions] ignoring unexpected registration for "${definition.id}"`)
        return
    }
    clearTimeout(waiting.timer)
    pending.delete(definition.id)
    waiting.resolve(definition)
}

/** Resolves when the extension `id` registers, rejects after `timeoutMs`. */
export function expectRegistration(id: string, timeoutMs: number): Promise<ExtensionDefinition> {
    return new Promise((resolve, reject) => {
        const timer = setTimeout(() => {
            pending.delete(id)
            reject(new Error(`extension "${id}" did not register within ${timeoutMs} ms`))
        }, timeoutMs)
        pending.set(id, { resolve, reject, timer })
    })
}

/** Rejects a pending registration (e.g. the script failed to load). */
export function failRegistration(id: string, error: Error): void {
    const waiting = pending.get(id)
    if (!waiting) return
    clearTimeout(waiting.timer)
    pending.delete(id)
    waiting.reject(error)
}
